import { useWeb3React } from '@web3-react/core'
import { ChainType, REQUEST_ETHEREUM_BALANCES_INTERVAL } from 'constants/chains'
import { useEffect, useMemo } from 'react'
import { Address, L1ChainId } from 'types'
import { isZeroAddress } from 'utils/address'
import { timer } from 'utils/timer'
import { useContracts, useWeb3Address } from '../../store/web3/hooks'
import { useTokenListByChainId } from '../zkex/useTokenListByChainId'
import { useChainType } from '../../store/connect/hooks'
import { callEthereumBalanceOf } from './multicall/balanceOf'
import { useProvider } from '@starknet-react/core'
import { callStarknetBalanceOf } from './multicall/starkBalanceOf'
import { NetworkRegistry } from '../../utils/networkRegistry'

export function useFetchEthereumBalance(l1ChainId: L1ChainId) {
  const address = useWeb3Address()
  const tokenList = useTokenListByChainId(l1ChainId)

  const tokenAddresses = useMemo(() => {
    return tokenList.map((v) => v.address)
  }, [tokenList])

  useFetchEthereumBalanceByAddresses(l1ChainId, address, tokenAddresses)
}

export function useFetchEthereumBalanceByAddresses(
  l1ChainId: L1ChainId,
  accountAddress: Address,
  tokenAddresses: Address[],
) {
  const contracts = useContracts()
  const { provider } = useWeb3React()
  const { provider: starkProvider } = useProvider()
  const chainType = useChainType()

  useEffect(() => {
    const { layerOneChainId = '' } = NetworkRegistry.byL1(l1ChainId) ?? {}

    if (
      layerOneChainId === undefined ||
      !contracts ||
      !tokenAddresses?.length ||
      !accountAddress ||
      isZeroAddress(accountAddress)
    ) {
      return
    }

    const multicallAddress = contracts.multicall[layerOneChainId]

    return timer(() => {
      if (chainType === ChainType.Starknet) {
        callStarknetBalanceOf({
          layerOneChainId,
          multicallAddress,
          provider: starkProvider!,
          accountAddress,
          tokenAddresses,
        })
      } else {
        callEthereumBalanceOf({
          layerOneChainId,
          multicallAddress,
          provider: provider!,
          accountAddress,
          tokenAddresses,
        })
      }
    }, REQUEST_ETHEREUM_BALANCES_INTERVAL)
  }, [
    contracts,
    l1ChainId,
    provider,
    starkProvider,
    tokenAddresses,
    accountAddress,
    chainType,
  ])
}
